
/** 认证相关函数完整性轮询间隔（毫秒） */
const INTEGRITY_INTERVAL_MS = 8000;

/** 匹配规则：字符串用 includes；RegExp 用 test（适配压缩后 return X()） */
type IntegrityMatchPattern = string | RegExp;

/**
 * CoreComponent.Base#isAuth 委托调用形态：
 * - 压缩：return Ln()
 * - 开发/Webpack：return (0, module.checkAuthAndRedirect)()
 */
const CORE_IS_AUTH_PATTERN = /return\s+(?:\(0\s*,[^)]+\)|[a-zA-Z_$][\w$]*)\s*\(\s*\)/;

/** checkAuthAndRedirect：须含门禁校验片段 */
const CHECK_AUTH_PATTERN = 'isGateValid';

type IntegrityTarget = {
    /** 获取当前待校验函数 */
    getFn: () => ((...args: any[]) => any) | undefined;
    /** 函数体须满足的片段或正则 */
    mustInclude: IntegrityMatchPattern;
};

type IntegrityBaseline = {
    hash: number;
};

let integrityTimer: ReturnType<typeof setInterval> | null = null;
let bootstrapped = false;

/**
 * 按规则判断源码：字符串 → includes；RegExp → test
 */
function matchesMustInclude(source: string, pattern: IntegrityMatchPattern): boolean {
    if (pattern instanceof RegExp) {
        return pattern.test(source);
    }
    return source.includes(pattern);
}

/**
 * 规范化函数源码（压缩空白，便于比对）
 */
function normalizeFnSource(fn: (...args: any[]) => any): string {
    return fn.toString().replace(/\s+/g, ' ').trim();
}

/**
 * 计算规范化后的函数字符串哈希
 */
function fnSourceHash(source: string): number {
    let hash = 0;
    for (let i = 0; i < source.length; i++) {
        hash = ((hash << 5) - hash) + source.charCodeAt(i);
        hash |= 0;
    }
    return hash;
}

/**
 * 安全失败：不暴露监控项名称或失败原因，避免攻击者定位防护逻辑
 */
function securityFail(): never {
    if (typeof document !== 'undefined') {
        document.body.innerHTML = 'Security violation';
    }
    throw new Error('Security violation');
}

/**
 * 模块加载时记录函数体基线；失败返回 null，由调用方统一 securityFail
 */
function captureBaseline(target: IntegrityTarget): IntegrityBaseline | null {
    const fn = target.getFn();
    if (!fn) {
        return null;
    }
    const source = normalizeFnSource(fn);
    if (!matchesMustInclude(source, target.mustInclude)) {
        return null;
    }
    return { hash: fnSourceHash(source) };
}

/**
 * 校验函数是否仍与基线一致
 */
function verifyBaseline(target: IntegrityTarget, baseline: IntegrityBaseline): boolean {
    const fn = target.getFn();
    if (!fn) {
        return false;
    }
    const source = normalizeFnSource(fn);
    console.log('verifyBaseline', source, target.mustInclude);
    if (!matchesMustInclude(source, target.mustInclude)) {
        return false;
    }
    return fnSourceHash(source) === baseline.hash;
}

/**
 * 启动认证函数完整性定时巡检（全局仅允许一次）
 */
function startAuthIntegrityWatch(targets: IntegrityTarget[]): void {
    if (integrityTimer) {
        return;
    }

    const baselines = targets.map(captureBaseline);
    if (baselines.some((b) => !b)) {
        securityFail();
    }

    const tick = () => {
        for (let i = 0; i < targets.length; i++) {
            if (!verifyBaseline(targets[i], baselines[i]!)) {
                securityFail();
            }
        }
    };

    tick();
    integrityTimer = setInterval(tick, INTEGRITY_INTERVAL_MS);
    window.addEventListener('focus', tick);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            tick();
        }
    });
}

/**
 * 应用级单点启动认证完整性巡检（在 core/index 调用一次即可）。
 * 监控 checkAuthAndRedirect 与 CoreComponent.Base#isAuth 原型方法。
 */
export function bootstrapAuthIntegrity(checkAuthAndRedirect: () => boolean, getCoreIsAuth: () => ((...args: any[]) => any) | undefined): void {
    if (bootstrapped || typeof window === 'undefined') {
        return;
    }
    bootstrapped = true;

    startAuthIntegrityWatch([
        {
            getFn: () => checkAuthAndRedirect,
            mustInclude: CHECK_AUTH_PATTERN,
        },
        {
            getFn: getCoreIsAuth,
            mustInclude: CORE_IS_AUTH_PATTERN,
        },
    ]);
}
